import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IoClose, IoCar } from "react-icons/io5";
import {
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaCarSide,
  FaMoneyBillWave,
  FaUser,
} from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import { cars, coupons, rentals } from "../../services/api";
import moment from "moment";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import logo from "../../assets/images/luxury-tours-luxury-transfers-black-leader-athens.png";


// Get the correct image from either the imported assets or the car object
// const getImage = (imagePath) => {
//   if (!imagePath) return null;

//   if (typeof imagePath === "string") {
//     // For database images with /images/ path
//     if (imagePath.startsWith("/images/")) {
//       const filename = imagePath.split("/").pop();
//       return imageMap[filename] || imagePath;
//     }
//     // For existing static imports
//     const filename = imagePath.split("/").pop();
//     return imageMap[filename] || imagePath;
//   }

//   return imagePath;
// };

const CreatePartnerRentalModal = ({ isOpen, onClose, partner, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [availableCars, setAvailableCars] = useState([]);
  const [selectedCar, setSelectedCar] = useState(null);
  const [selectedCarDetails, setSelectedCarDetails] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState({
    pickup: false,
    dropoff: false,
  });
  const [dateTimeComplete, setDateTimeComplete] = useState({
    pickup: false,
    dropoff: false,
  });
  const [timeWarnings, setTimeWarnings] = useState({
    pickup: false,
    dropoff: false,
  });
  const [availabilityData, setAvailabilityData] = useState(null);
  const [couponStatus, setCouponStatus] = useState({
    isValid: false,
    discount: 0,
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [totalPrice, setTotalPrice] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [durationDiscounts, setDurationDiscounts] = useState({
    shortTerm: { discount: 0 },
    mediumTerm: { discount: 5 },
    longTerm: { discount: 10 },
  });

  const [formData, setFormData] = useState({
    pickupDate: null,
    dropoffDate: null,
    pickupLocation: "",
    dropoffLocation: "",
    customPickupLocation: "",
    customDropoffLocation: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    age: "26+",
    additionalOptions: {
      childSeat: false,
      babyChair: false,
      gps: false,
    },
    couponCode: "",
    notes: "",
  });

  const [sameLocation, setSameLocation] = useState(true);

  const locationOptions = [
    { value: "airport", label: "Airport of Athens (JMK)" },
    { value: "central", label: "Athens Central Office" },
    { value: "new-port", label: "Mykonos New Port (Tourlos)" },
    { value: "old-port", label: "Mykonos Old Port" },
    { value: "hotel", label: "Hotel / Apartment / Villa" },
    { value: "other", label: "Other Location (Hotel/Restaurant/Beach)" },
  ];

  useEffect(() => {
    if (isOpen) {
      fetchCars();
      fetchDurationDiscounts();
    } else {
      // Reset the form when closing
      resetForm();
    }
  }, [isOpen]);

  const fetchDurationDiscounts = async () => {
    try {
      const response = await rentals.getDurationDiscounts();
      setDurationDiscounts(response.data);
    } catch (error) {
      console.error("Error fetching duration discounts:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      pickupDate: null,
      dropoffDate: null,
      pickupLocation: "",
      dropoffLocation: "",
      customPickupLocation: "",
      customDropoffLocation: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      age: "26+",
      additionalOptions: {
        childSeat: false,
        babyChair: false,
        gps: false,
      },
      couponCode: "",
      notes: "",
    });
    setSameLocation(true);
    setSelectedCar(null);
    setSelectedCarDetails(null);
    setAvailabilityData(null);
    setCouponStatus({ isValid: false, discount: 0, message: "" });
    setErrors({});
    setTotalPrice(0);
    setDatePickerOpen({ pickup: false, dropoff: false });
    setDateTimeComplete({ pickup: false, dropoff: false });
    setTimeWarnings({ pickup: false, dropoff: false });
  };

  const fetchCars = async () => {
    try {
      setInitialLoading(true);
      const response = await cars.getAll();
      setAvailableCars(
        response.data.filter((car) => car.status === "available")
      );
      setInitialLoading(false);
    } catch (error) {
      console.error("Error fetching cars:", error);
      setInitialLoading(false);
    }
  };

  const fetchCarDetails = async (carId) => {
    try {
      setLoading(true);
      const response = await cars.getById(carId);
      setSelectedCarDetails(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching car details:", error);
      setLoading(false);
    }
  };

  const handleCarSelect = (car) => {
    setSelectedCar(car);
    fetchCarDetails(car._id);
    fetchInitialPricing(car.category, car.stringId);
  };

  const fetchInitialPricing = async (category, stringId) => {
    try {
      const today = new Date();
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);

      const response = await cars.getAvailable({
        startDate: monthStart.toISOString(),
        endDate: monthEnd.toISOString(),
        category: category,
      });

      setAvailabilityData({
        dynamicPrices:
          response.data.find((c) => c.stringId === stringId)?.availability ||
          [],
      });
    } catch (error) {
      console.error("Error fetching initial pricing:", error);
    }
  };

  const handleMonthChange = async (date) => {
    if (!selectedCar) return;

    try {
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);

      const response = await cars.getAvailable({
        startDate: monthStart.toISOString(),
        endDate: monthEnd.toISOString(),
        category: selectedCar.category,
      });

      setAvailabilityData({
        dynamicPrices:
          response.data.find((c) => c.stringId === selectedCar.stringId)
            ?.availability || [],
      });
    } catch (error) {
      console.error("Error fetching pricing for month:", error);
    }
  };

  const validateDates = (newDate, type) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (type === "pickupDate") {
      if (formData.dropoffDate && newDate > formData.dropoffDate) {
        setErrors((prev) => ({
          ...prev,
          pickupDate: "Pick-up date cannot be after drop-off date",
        }));
        return false;
      }
      if (newDate < today) {
        setErrors((prev) => ({
          ...prev,
          pickupDate: "Pick-up date cannot be in the past",
        }));
        return false;
      }
    } else if (type === "dropoffDate") {
      if (formData.pickupDate && newDate < formData.pickupDate) {
        setErrors((prev) => ({
          ...prev,
          dropoffDate: "Drop-off date cannot be before pick-up date",
        }));
        return false;
      }
      if (newDate < today) {
        setErrors((prev) => ({
          ...prev,
          dropoffDate: "Drop-off date cannot be in the past",
        }));
        return false;
      }
    }

    setErrors((prev) => ({ ...prev, [type]: undefined }));
    return true;
  };

  const handleDateChange = (date, type) => {
    if (!validateDates(date, type)) {
      return;
    }

    const isPickup = type === "pickupDate";
    const stateKey = isPickup ? "pickup" : "dropoff";

    // Check if both date and time are selected
    const hasTime = date && date.getHours() !== 0;
    
    setDateTimeComplete((prev) => ({
      ...prev,
      [stateKey]: hasTime,
    }));

    // Show warning if time is not selected
    setTimeWarnings(prev => ({
      ...prev,
      [stateKey]: !hasTime
    }));

    setFormData((prev) => ({
      ...prev,
      [type]: date,
    }));

    // Only close if time is selected
    if (hasTime) {
      setDatePickerOpen((prev) => ({
        ...prev,
        [stateKey]: false,
        // If pickup date is set, open dropoff picker
        ...(isPickup ? { dropoff: true } : {}),
      }));
    }
  };

  const calculatePriceBreakdown = () => {
    if (!formData.pickupDate || !formData.dropoffDate || !selectedCar) {
      return null;
    }

    const breakdown = [];
    let currentDate = new Date(formData.pickupDate);
    const endDate = new Date(formData.dropoffDate);
    endDate.setHours(23, 59, 59);

    while (currentDate <= endDate) {
      const dateString = currentDate.toDateString();

      // Get dynamic price if available
      const dynamicPriceData = availabilityData?.dynamicPrices?.find(
        (p) => new Date(p.date).toDateString() === dateString
      );

      const price = dynamicPriceData?.dynamicPrice || selectedCar.basePrice;

      // Try to merge with previous entry if same price
      const lastEntry =
        breakdown.length > 0 ? breakdown[breakdown.length - 1] : null;
      if (lastEntry && lastEntry.price === price) {
        lastEntry.days++;
        lastEntry.endDate = new Date(currentDate);
      } else {
        breakdown.push({
          startDate: new Date(currentDate),
          endDate: new Date(currentDate),
          price,
          days: 1,
        });
      }

      currentDate = new Date(currentDate.setDate(currentDate.getDate() + 1));
    }

    return breakdown;
  };

  const calculateTotalPrice = useCallback(() => {
    if (!formData.pickupDate || !formData.dropoffDate || !selectedCar) {
      return 0;
    }

    const breakdown = calculatePriceBreakdown();
    if (!breakdown) return selectedCar.basePrice || 0;

    const subtotal = breakdown.reduce(
      (total, period) => total + period.price * period.days,
      0
    );

    const totalDays = Math.ceil(
      Math.abs(new Date(formData.dropoffDate) - new Date(formData.pickupDate)) /
        (1000 * 60 * 60 * 24)
    );

    // Apply duration-based discounts using server configuration
    let discountPercentage = 0;
    if (totalDays > 10) {
      discountPercentage = durationDiscounts.longTerm.discount;
    } else if (totalDays > 3) {
      discountPercentage = durationDiscounts.mediumTerm.discount;
    } else {
      discountPercentage = durationDiscounts.shortTerm.discount;
    }

    const discount = (subtotal * discountPercentage) / 100;
    return subtotal - discount;
  }, [
    formData.pickupDate,
    formData.dropoffDate,
    selectedCar,
    availabilityData,
    durationDiscounts,
  ]);

  useEffect(() => {
    if (formData.pickupDate && formData.dropoffDate && selectedCar) {
      const basePrice = calculateTotalPrice();
      const netTotal = basePrice / 1.24; // Remove VAT
      const netAfterDiscount =
        netTotal - (couponStatus.isValid ? couponStatus.discount : 0);
      const finalTotal = netAfterDiscount * 1.24; // Apply VAT
      setTotalPrice(finalTotal);
    } else {
      setTotalPrice(0);
    }
  }, [
    formData.pickupDate,
    formData.dropoffDate,
    selectedCar,
    calculateTotalPrice,
    couponStatus,
  ]);

  const validateCoupon = async (code) => {
    if (!code) {
      setCouponStatus({ isValid: false, discount: 0, message: "" });
      return;
    }

    try {
      const finalPrice = calculateTotalPrice();
      const response = await coupons.validate(code, finalPrice / 1.24); // Pass net amount
      setCouponStatus({
        isValid: true,
        discount: response.data.discount,
        message: "✓ Coupon applied successfully!",
      });
    } catch (error) {
      setCouponStatus({
        isValid: false,
        discount: 0,
        message: "✕ Invalid coupon code",
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!selectedCar) newErrors.car = "Please select a car";
    if (!formData.pickupDate)
      newErrors.pickupDate = "Pick-up date and time are required";
    if (!formData.dropoffDate)
      newErrors.dropoffDate = "Drop-off date and time are required";
    if (!formData.firstName) newErrors.firstName = "First name is required";
    if (!formData.lastName) newErrors.lastName = "Last name is required";
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Invalid email address";
    }
    if (!formData.phone) newErrors.phone = "Phone number is required";
    if (!formData.pickupLocation) {
      newErrors.pickupLocation = "Pick-up location is required";
    } else if (
      formData.pickupLocation === "other" &&
      !formData.customPickupLocation
    ) {
      newErrors.pickupLocation = "Please enter the custom pick-up location";
    }

    if (!sameLocation) {
      if (!formData.dropoffLocation) {
        newErrors.dropoffLocation = "Drop-off location is required";
      } else if (
        formData.dropoffLocation === "other" &&
        !formData.customDropoffLocation
      ) {
        newErrors.dropoffLocation = "Please enter the custom drop-off location";
      }
    }

    if (formData.pickupDate && formData.dropoffDate) {
      if (formData.pickupDate >= formData.dropoffDate) {
        newErrors.dropoffDate = "Drop-off time must be after pick-up time";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatDateRange = (date) => {
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
    });
  };

  const renderDayContents = (day, date) => {
    if (!selectedCar) return day;

    const dayDate = new Date(date);

    // Find dynamic price for this date if available
    const dynamicPriceData = availabilityData?.dynamicPrices?.find(
      (p) => new Date(p.date).toDateString() === dayDate.toDateString()
    );

    const dayPrice = dynamicPriceData?.dynamicPrice || selectedCar.basePrice;

    return (
      <div className="relative py-4 mx-auto text-center">
        <span>{dayDate.getDate()}</span>
        {dayPrice && (
          <div
            className={`absolute bottom-0 left-0 right-0 text-[10px] ${
              dynamicPriceData ? "text-blue-600" : "text-green-600"
            } font-bold`}
          >
            €{dayPrice}
          </div>
        )}
      </div>
    );
  };

  const colors = {
    primary: "#D81919",
    secondary: "#990000",
    accent: "#FFEDED",
    dark: "#333333",
    light: "#FFFFFF",
    gray: "#F8F8F8",
  };

const generateInvoice = async (rental) => {
  const invoiceDiv = document.createElement("div");
  invoiceDiv.style.padding = "10px";
  invoiceDiv.style.position = "absolute";
  invoiceDiv.style.left = "-9999px";
  invoiceDiv.style.background = "white";
  invoiceDiv.style.width = "900px";

  const header = `
  <div style="display: flex; justify-content: space-between; margin-bottom: 40px; position: relative;">
    <div style="width: 66%; position: relative; overflow: hidden; height: 120px;">
      <div style="position: absolute; top: -20px; left: -200px; width: calc(100% + 220px); height: calc(100% + 40px); transform: skew(-15deg); z-index: 0; box-shadow: 0 3px 10px rgba(0,0,0,0.2);"></div>
      <img src="${logo}" alt="Logo" style="width: 85%; height: 100%; object-fit: contain; position: relative; z-index: 1; padding: 20px 20px 20px 40px;" />
    </div>
    <div style="width: 32%; text-align: right; padding: 20px; background: ${
      colors.gray
    }; border-right: 5px solid ${
    colors.primary
  }; box-shadow: 0 3px 8px rgba(0,0,0,0.1);">
      <h2 style="margin-bottom: 15px; color: ${
        colors.primary
      }; font-size: 22px; font-weight: bold;">Contact Information</h2>
      <p style="margin: 5px 0; color: ${
        colors.dark
      };">Epar. Odos Mykonos 846 00</p>
      <p style="margin: 5px 0; color: ${colors.dark};">nleont@gmail.com</p>
      <p style="margin: 5px 0; color: ${
        colors.dark
      }; font-weight: bold;">+306932214626</p>
      <p style="margin: 5px 0; color: ${
        colors.primary
      };">${window.location.origin}</p>
    </div>
  </div>
  <div style="text-align: center; margin-bottom: 30px; padding: 10px 0; border-bottom: 2px solid ${
    colors.primary
  }; border-top: 2px solid ${colors.primary};">
    <h1 style="margin: 0; color: ${
      colors.primary
    }; font-size: 28px; text-transform: uppercase; letter-spacing: 2px;">PRE-INVOICE</h1>
    <p style="margin: 5px 0; color: ${
      colors.dark
    };">Pre-Invoice #: INV-${new Date().getFullYear()}-${Math.floor(
    Math.random() * 1000000
  )}</p>
    <p style="margin: 5px 0; color: ${
      colors.dark
    };">Pre-Invoice Date: ${new Date().toLocaleDateString("en-GB")}</p>
  </div>
`;

  const infoSection = `
  <div style="display: flex; justify-content: space-between; margin-bottom: 40px; gap: 40px;">
    <div style="flex: 1; background: ${
      colors.accent
    }; padding: 25px; border-radius: 8px; border-left: 6px solid ${
    colors.primary
  }; box-shadow: 0 3px 10px rgba(0,0,0,0.1);">
      <h3 style="margin-bottom: 15px; color: ${
        colors.primary
      }; text-transform: uppercase; font-weight: bold; border-bottom: 2px solid ${
    colors.primary
  }; padding-bottom: 8px;">CLIENT DETAILS</h3>
      <p style="margin: 10px 0; font-weight: bold; font-size: 16px;">${
        rental.customer.firstName
      } ${rental.customer.lastName}</p>
      <p style="margin: 8px 0;"><span style="color: ${
        colors.secondary
      }; font-weight: bold;">Phone:</span> ${rental.customer.phoneNumber}</p>
      <p style="margin: 8px 0;"><span style="color: ${
        colors.secondary
      }; font-weight: bold;">Email:</span> ${rental.customer.email}</p>
      <p style="margin: 8px 0;"><span style="color: ${
        colors.secondary
      }; font-weight: bold;">Age:</span> ${rental.customer.age}</p>
    </div>
    <div style="flex: 1; background: ${
      colors.accent
    }; padding: 25px; border-radius: 8px; border-left: 6px solid ${
    colors.primary
  }; box-shadow: 0 3px 10px rgba(0,0,0,0.1);">
      <h3 style="margin-bottom: 15px; color: ${
        colors.primary
      }; text-transform: uppercase; font-weight: bold; border-bottom: 2px solid ${
    colors.primary
  }; padding-bottom: 8px;">VEHICLE INFORMATION</h3>
      <p style="margin: 10px 0; font-weight: bold; font-size: 16px;"><span style="color: ${
        colors.secondary
      }; font-weight: bold;">Vehicle Name:</span> ${rental.carName}</p>
      <p style="margin: 8px 0;"><span style="color: ${
        colors.secondary
      }; font-weight: bold;">Rental Period:</span> ${new Date(
    rental.pickupDateTime
  ).toLocaleDateString("en-GB")} - ${new Date(
    rental.returnDateTime
  ).toLocaleDateString("en-GB")}</p>
      <p style="margin: 8px 0;"><span style="color: ${
        colors.secondary
      }; font-weight: bold;">Pick-up:</span> ${rental.pickupLocation}</p>
      <p style="margin: 8px 0;"><span style="color: ${
        colors.secondary
      }; font-weight: bold;">Return:</span> ${rental.returnLocation}</p>
    </div>
  </div>
`;

  const priceRows = rental.priceBreakdown
    .map(
      (period, index) => `
  <tr style="background-color: ${
    index % 2 === 0 ? colors.light : colors.accent
  };">
    <td style="padding: 14px; border-bottom: 1px solid #ddd; font-weight: bold;">
      Rental (${new Date(period.startDate).toLocaleDateString(
        "en-GB"
      )} - ${new Date(period.endDate).toLocaleDateString("en-GB")})
    </td>
    <td style="padding: 14px; border-bottom: 1px solid #ddd; text-align: center;">€${
      period.pricePerDay
    }</td>
    <td style="padding: 14px; border-bottom: 1px solid #ddd; text-align: center;">${
      period.numberOfDays
    }</td>
    <td style="padding: 14px; border-bottom: 1px solid #ddd; text-align: right; font-weight: bold;">€${
      period.totalForPeriod
    }</td>
  </tr>
`
    )
    .join("");

  const additionalOptionsRows = [];
  if (rental.additionalOptions) {
    if (rental.additionalOptions.childSeat)
      additionalOptionsRows.push(
        '<tr><td style="padding: 12px; border: 2px solid #ddd;">Child Seat</td><td style="padding: 12px; border: 2px solid #ddd; text-align: center;">Free</td><td style="padding: 12px; border: 2px solid #ddd; text-align: center;">1</td><td style="padding: 12px; border: 2px solid #ddd; text-align: right;">€0.00</td></tr>'
      );
    if (rental.additionalOptions.babySeat)
      additionalOptionsRows.push(
        '<tr><td style="padding: 12px; border: 2px solid #ddd;">Baby Seat</td><td style="padding: 12px; border: 2px solid #ddd; text-align: center;">Free</td><td style="padding: 12px; border: 2px solid #ddd; text-align: center;">1</td><td style="padding: 12px; border: 2px solid #ddd; text-align: right;">€0.00</td></tr>'
      );
    if (rental.additionalOptions.gps)
      additionalOptionsRows.push(
        '<tr><td style="padding: 12px; border: 2px solid #ddd;">GPS</td><td style="padding: 12px; border: 2px solid #ddd; text-align: center;">Free</td><td style="padding: 12px; border: 2px solid #ddd; text-align: center;">1</td><td style="padding: 12px; border: 2px solid #ddd; text-align: right;">€0.00</td></tr>'
      );
  }

  const priceTable = `
  <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px; border: 2px solid ${
    colors.primary
  }; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
    <thead>
      <tr style="background: ${colors.primary}; color: white;">
        <th style="padding: 15px; text-align: left; font-size: 15px;">Description</th>
        <th style="padding: 15px; text-align: center; font-size: 15px;">Rate</th>
        <th style="padding: 15px; text-align: center; font-size: 15px;">Quantity</th>
        <th style="padding: 15px; text-align: right; font-size: 15px;">Amount</th>
      </tr>
    </thead>
    <tbody>
      ${priceRows}
      ${additionalOptionsRows.join("")}
    </tbody>
  </table>
`;

  const additionalNotesSection = rental.additionalNotes
    ? `
    <div style="margin-bottom: 20px; background: #f8f8f8; padding: 15px; border-left: 4px solid #1e3a8a;">
      <h4 style="margin-bottom: 10px; color: #1e3a8a; text-transform: uppercase; font-weight: bold;">Additional Notes</h4>
      <p style="margin: 0; font-style: italic;">${rental.additionalNotes}</p>
    </div>
  `
    : "";

  const totalsSection = `
  <div style="display: flex; justify-content: flex-end;">
    <div style="width: 350px;">
      <div style="border: 2px solid ${
        colors.primary
      }; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
        <div style="padding: 14px; border-bottom: 1px solid #ddd; background: ${
          colors.accent
        };">
          <div style="display: flex; justify-content: space-between;">
            <span style="font-weight: bold;">Subtotal:</span>
            <span>€${(rental.totalPrice / 1.24).toFixed(2)}</span>
          </div>
        </div>
        ${
          rental.discountAmount
            ? `
        <div style="padding: 14px; border-bottom: 1px solid #ddd; background: ${
          colors.accent
        }; color: ${colors.primary};">
          <div style="display: flex; justify-content: space-between;">
            <span style="font-weight: bold;">Discount:</span>
            <span>-€${rental.discountAmount.toFixed(2)}</span>
          </div>
        </div>
        `
            : ""
        }
        <div style="padding: 14px; border-bottom: 1px solid #ddd; background: ${
          colors.accent
        };">
          <div style="display: flex; justify-content: space-between;">
            <span style="font-weight: bold;">VAT (24%):</span>
            <span>€${(rental.totalPrice - rental.totalPrice / 1.24).toFixed(
              2
            )}</span>
          </div>
        </div>
        <div style="padding: 16px; background: ${
          colors.primary
        }; color: white;">
          <div style="display: flex; justify-content: space-between; font-weight: bold; font-size: 18px;">
            <span>TOTAL:</span>
            <span>€${rental.totalPrice.toFixed(2)} (NOT PAID)</span>
          </div>
        </div>
      </div>
    </div>
  </div>
`;
  const footer = `
  <div style="margin-top: 50px;">
    <div style="margin-bottom: 30px; padding: 20px; background: ${colors.accent}; border-radius: 8px; border-left: 6px solid ${colors.primary}; box-shadow: 0 3px 10px rgba(0,0,0,0.1);">
      <p style="margin: 0; font-style: italic; color: ${colors.dark};">Thank you for choosing <span style="font-weight: bold; color: ${colors.primary};">Black Leader Transfers Athens</span>. For assistance, contact us at <span style="font-weight: bold;">+306932214626</span> or <span style="font-weight: bold;">blackleaderathens@gmail.com</span></p>
    </div>
    <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 30px;">
      <div style="width: 30%;">
        <div style="background: ${colors.primary}; height: 4px; margin-bottom: 5px;"></div>
        <div style="background: ${colors.primary}; height: 2px;"></div>
      </div>
      <div style="width: 200px; text-align: center;">
        <div style="border-bottom: 2px solid ${colors.primary}; margin-bottom: 8px; padding-bottom: 15px;">
          <img src="data:image/png;base64,..." alt="Signature" style="max-width: 150px;" />
        </div>
        <p style="margin: 0; font-weight: bold; color: ${colors.primary};">Nikos Leontitsis</p>
        <p style="margin: 0; color: ${colors.dark};">Managing Director</p>
      </div>
      <div style="width: 30%;">
        <div style="background: ${colors.primary}; height: 4px; margin-bottom: 5px;"></div>
        <div style="background: ${colors.primary}; height: 2px;"></div>
      </div>
    </div>
  </div>
  <div style="margin-top: 40px; text-align: center; padding-top: 20px; border-top: 2px dashed ${colors.accent};">
  </div>
`;

  invoiceDiv.innerHTML =
    header +
    infoSection +
    priceTable +
    additionalNotesSection +
    totalsSection +
    footer;
  document.body.appendChild(invoiceDiv);

  try {
    const scale = 2;
    const canvas = await html2canvas(invoiceDiv, {
      scale: scale,
      logging: false,
      useCORS: true,
      allowTaint: true,
    });

    const imgData = canvas.toDataURL("image/jpeg", 1.0);
    const pdf = new jsPDF("p", "pt", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = canvas.width / scale;
    const imgHeight = canvas.height / scale;
    const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
    const imgX = (pdfWidth - imgWidth * ratio) / 2;
    const imgY = 20;

    pdf.addImage(
      imgData,
      "JPEG",
      imgX,
      imgY,
      imgWidth * ratio,
      imgHeight * ratio
    );

    // Add page numbers at the bottom
    const pageCount = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.setFontSize(10);
      pdf.setTextColor(150);
    }

    // Instead of saving, return the PDF as base64
    const pdfData = pdf.output("datauristring");
    return pdfData.split(",")[1]; // Return just the base64 data
  } finally {
    document.body.removeChild(invoiceDiv);
  }
};

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Build rental data
      const effectivePickupLocation =
        formData.pickupLocation === "other"
          ? formData.customPickupLocation
          : formData.pickupLocation;
      const effectiveDropoffLocation = sameLocation
        ? formData.pickupLocation === "other"
          ? formData.customPickupLocation
          : formData.pickupLocation
        : formData.dropoffLocation === "other"
        ? formData.customDropoffLocation
        : formData.dropoffLocation;

      // Calculate final price
      const basePrice = calculateTotalPrice();
      const netTotal = basePrice / 1.24; // Remove VAT
      const discountAmount = couponStatus.isValid ? couponStatus.discount : 0;
      const netAfterDiscount = netTotal - discountAmount;
      const finalTotal = netAfterDiscount * 1.24; // Apply VAT

      // Format price breakdown
      const priceBreakdown = calculatePriceBreakdown()?.map((period) => ({
        startDate: period.startDate.toISOString(),
        endDate: period.endDate.toISOString(),
        pricePerDay: period.price,
        numberOfDays: period.days,
        totalForPeriod: period.price * period.days,
      }));

      // Calculate commission amount based on partner's rate
      const commissionAmount =
        (netAfterDiscount * partner.commissionRate) / 100;

      const rentalData = {
        car: selectedCar.stringId,
        carName: `${selectedCar.make} ${selectedCar.model}`,
        customer: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phoneNumber: formData.phone,
          age: formData.age,
        },
        pickupLocation: effectivePickupLocation,
        returnLocation: effectiveDropoffLocation,
        pickupDateTime: new Date(formData.pickupDate).toISOString(),
        returnDateTime: new Date(formData.dropoffDate).toISOString(),
        additionalOptions: {
          childSeat: formData.additionalOptions.childSeat,
          babySeat: formData.additionalOptions.babyChair,
          gps: formData.additionalOptions.gps,
        },
        couponCode: formData.couponCode || undefined,
        basePrice: selectedCar.basePrice,
        totalPrice: finalTotal,
        discountAmount,
        priceBreakdown,
        additionalNotes: formData.notes,
        professionalPartnerId: partner._id, // Add partner ID
        commissionAmount: commissionAmount, // Add calculated commission amount
        status: "confirmed", // Auto-confirm rentals created by admin
      };

      // Generate PDF data first
      const pdfData = await generateInvoice(rentalData);

      // Add PDF data to rental data
      rentalData.invoicePdf = pdfData;

      const response = await rentals.create(rentalData);
      onSuccess(response.data);
      onClose();
    } catch (error) {
      console.error("Error creating rental:", error);
      setErrors({ submit: "Failed to create rental. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const CustomInputComponent = ({
    value,
    onClick,
    placeholder,
    isError,
    icon: Icon,
  }) => (
    <div
      className={`w-full relative cursor-pointer ${
        isError ? "border-red-500" : "border-gray-300"
      }`}
    >
      <button
        type="button"
        className={`w-full flex items-center px-4 py-2 border ${
          isError ? "border-red-500 bg-red-50" : "border-gray-300"
        } rounded-md transition-all hover:border-blue-500`}
        onClick={onClick}
      >
        {Icon && <Icon className="text-gray-500 mr-2" />}
        {value || placeholder}
      </button>
    </div>
  );

  const CustomSelect = ({
    value,
    onChange,
    onOtherLocationChange,
    options,
    placeholder,
    showOtherInput = false,
    initialCustomValue = "",
  }) => {
    const [showOtherInputField, setShowOtherInputField] = useState(
      value === "other"
    );

    useEffect(() => {
      setShowOtherInputField(value === "other" && showOtherInput);
    }, [value, showOtherInput]);

    return (
      <div>
        <div className="relative">
          <select
            value={value}
            onChange={(e) => {
              onChange(e);
              setShowOtherInputField(
                e.target.value === "other" && showOtherInput
              );
            }}
            className="w-full h-10 pl-3 pr-10 border border-gray-300 rounded bg-white hover:border-blue-500 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
          >
            <option value="">{placeholder}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
            <svg
              className="w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M19 9l-7 7-7-7"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              />
            </svg>
          </div>
        </div>
        {showOtherInputField && (
          <input
            type="text"
            placeholder="Enter location details"
            defaultValue={initialCustomValue}
            onChange={(e) => onOtherLocationChange(e.target.value)}
            className="w-full h-10 px-3 mt-2 border border-gray-200 rounded bg-white hover:border-blue-500 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        )}
      </div>
    );
  };

  const CustomCheckbox = ({ checked, onChange, label }) => (
    <label className="flex items-center cursor-pointer group hover:bg-gray-50 p-2 rounded-md transition-colors">
      <div className="relative w-5 h-5 mr-3">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="opacity-0 absolute h-5 w-5 cursor-pointer"
        />
        <div
          className={`border-2 rounded-sm h-5 w-5 flex flex-shrink-0 justify-center items-center focus-within:border-blue-500 ${
            checked ? "bg-blue-500 border-blue-500" : "border-gray-300"
          }`}
        >
          {checked && (
            <svg
              className="fill-current w-3 h-3 text-white pointer-events-none"
              viewBox="0 0 20 20"
            >
              <path d="M0 11l2-2 5 5L18 3l2 2L7 18z" />
            </svg>
          )}
        </div>
      </div>
      <span className="select-none text-gray-700">{label}</span>
    </label>
  );

  const filteredCars = availableCars.filter((car) => {
    const matchesSearch =
      !searchQuery ||
      `${car.make} ${car.model}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
    const matchesCategory =
      !selectedCategory || car.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-6xl relative max-h-[90vh] overflow-hidden m-4"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-900 to-blue-800 px-6 py-4 flex justify-between items-center sticky top-0 z-20">
          <div className="flex items-center space-x-3">
            <motion.div
              className="p-2 bg-blue-800/50 rounded-full"
              initial={{ rotate: 0 }}
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            >
              <FaCarSide className="text-white text-2xl" />
            </motion.div>
            <h2 className="text-xl font-bold text-white">
              Create Rental for {partner?.firstName}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-blue-800/50 rounded-full transition-colors text-white"
          >
            <IoClose size={24} />
          </button>
        </div>

        {/* Content */}
        {initialLoading ? (
          <div className="p-8 flex flex-col items-center justify-center">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-gray-500">Loading cars...</p>
          </div>
        ) : (
          <div className="overflow-y-auto max-h-[calc(90vh-4rem)]">
            <div className="p-6">
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Car Selection Column */}
                  <div className="lg:col-span-1">
                    <h3 className="text-lg font-semibold mb-4">Select a Car</h3>

                    {/* Search and Filter Controls */}
                    <div className="sticky top-0 bg-white z-10 space-y-3 mb-4 p-4 border border-gray-200 rounded-lg shadow-sm">
                      <input
                        type="text"
                        placeholder="Search cars..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />

                      <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">All Categories</option>
                        {Array.from(
                          new Set(availableCars.map((car) => car.category))
                        ).map((category) => (
                          <option key={category} value={category}>
                            {category}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="h-[calc(100vh-20rem)] overflow-y-auto border border-gray-200 rounded-lg">
                      <div className="divide-y divide-gray-200">
                        {filteredCars.map((car) => (
                          <div
                            key={car._id}
                            className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                              selectedCar?._id === car._id ? "bg-blue-50" : ""
                            }`}
                            onClick={() => handleCarSelect(car)}
                          >
                            <div className="flex items-center space-x-4">
                              <div className="flex-shrink-0 h-14 w-14 bg-gray-100 rounded-md flex items-center justify-center overflow-hidden">
                                {car.images && car.images[0] ? (
                                  <></>
                                  // <img
                                  //   src={getImage(car.images[0])}
                                  //   alt={`${car.make} ${car.model}`}
                                  //   className="h-full w-full object-contain"
                                  //   onError={(e) => {
                                  //     e.target.onerror = null;
                                  //     e.target.src =
                                  //       "/images/placeholder-car.png";
                                  //   }}
                                  // />
                                ) : (
                                  <FaCarSide className="text-gray-400 text-2xl" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {car.make} {car.model}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {car.year} • {car.category}
                                </p>
                                <div className="flex items-center mt-1">
                                  <span className="text-sm font-semibold text-blue-900">
                                    €{car.basePrice}/day
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                        {filteredCars.length === 0 && (
                          <div className="p-6 text-center text-gray-500">
                            No cars found matching your criteria
                          </div>
                        )}
                      </div>
                    </div>
                    {errors.car && (
                      <p className="mt-1 text-sm text-red-500">{errors.car}</p>
                    )}
                  </div>

                  {/* Rental Details Column */}
                  <div className="lg:col-span-2 space-y-6">
                    <h3 className="text-lg font-semibold mb-4">
                      Rental Details
                    </h3>

                    {loading ? (
                      <div className="flex items-center justify-center py-8">
                        <LoadingSpinner />
                      </div>
                    ) : (
                      <>
                        {/* Date Selection */}
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                          <h4 className="font-medium text-gray-900 mb-4">
                            Rental Period
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Pick-up Date & Time *
                              </label>
                              <DatePicker
                                selected={formData.pickupDate}
                                onChange={(date) =>
                                  handleDateChange(date, "pickupDate")
                                }
                                onMonthChange={handleMonthChange}
                                customInput={
                                  <CustomInputComponent
                                    placeholder="Pick-up Date & Time"
                                    isError={!!errors.pickupDate}
                                    icon={FaCalendarAlt}
                                  />
                                }
                                dateFormat="dd/MM/yyyy HH:mm"
                                showTimeSelect
                                timeFormat="HH:mm"
                                timeIntervals={30}
                                minDate={new Date()}
                                placeholderText="Pick-up Date & Time"
                                open={datePickerOpen.pickup}
                                onClickOutside={() => {
                                  if (dateTimeComplete.pickup) {
                                    setDatePickerOpen((prev) => ({
                                      ...prev,
                                      pickup: false,
                                    }));
                                  } else {
                                    setTimeWarnings((prev) => ({
                                      ...prev,
                                      pickup: true,
                                    }));
                                  }
                                }}
                                wrapperClassName="w-full"
                                onInputClick={() =>
                                  setDatePickerOpen((prev) => ({
                                    ...prev,
                                    pickup: true,
                                  }))
                                }
                                renderDayContents={renderDayContents}
                                disabled={!selectedCar}
                              />
                              {errors.pickupDate && (
                                <p className="text-red-500 text-xs mt-1">
                                  {errors.pickupDate}
                                </p>
                              )}
                              {timeWarnings.pickup && (
                                <p className="text-yellow-500 text-xs mt-1">
                                  Please select a time for pick-up.
                                </p>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Drop-off Date & Time *
                              </label>
                              <DatePicker
                                selected={formData.dropoffDate}
                                onChange={(date) =>
                                  handleDateChange(date, "dropoffDate")
                                }
                                onMonthChange={handleMonthChange}
                                customInput={
                                  <CustomInputComponent
                                    placeholder="Drop-off Date & Time"
                                    isError={!!errors.dropoffDate}
                                    icon={FaCalendarAlt}
                                  />
                                }
                                dateFormat="dd/MM/yyyy HH:mm"
                                showTimeSelect
                                timeFormat="HH:mm"
                                timeIntervals={30}
                                minDate={formData.pickupDate || new Date()}
                                placeholderText="Drop-off Date & Time"
                                open={datePickerOpen.dropoff}
                                onClickOutside={() => {
                                  if (dateTimeComplete.dropoff) {
                                    setDatePickerOpen((prev) => ({
                                      ...prev,
                                      dropoff: false,
                                    }));
                                  } else {
                                    setTimeWarnings((prev) => ({
                                      ...prev,
                                      dropoff: true,
                                    }));
                                  }
                                }}
                                wrapperClassName="w-full"
                                onInputClick={() =>
                                  setDatePickerOpen((prev) => ({
                                    ...prev,
                                    dropoff: true,
                                  }))
                                }
                                renderDayContents={renderDayContents}
                                disabled={!selectedCar}
                              />
                              {errors.dropoffDate && (
                                <p className="text-red-500 text-xs mt-1">
                                  {errors.dropoffDate}
                                </p>
                              )}
                              {timeWarnings.dropoff && (
                                <p className="text-yellow-500 text-xs mt-1">
                                  Please select a time for drop-off.
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Locations */}
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                          <h4 className="font-medium text-gray-900 mb-4">
                            Locations
                          </h4>
                          <div className="flex justify-end mb-2">
                            <label className="flex items-center text-xs sm:text-sm text-gray-600">
                              <input
                                type="checkbox"
                                checked={sameLocation}
                                onChange={(e) => {
                                  setSameLocation(e.target.checked);
                                  if (e.target.checked) {
                                    setFormData((prev) => ({
                                      ...prev,
                                      dropoffLocation: prev.pickupLocation,
                                      customDropoffLocation:
                                        prev.customPickupLocation,
                                    }));
                                  }
                                }}
                                className="mr-1.5 sm:mr-2 w-3 h-3 sm:w-4 sm:h-4"
                                disabled={!selectedCar}
                              />
                              <span className="whitespace-nowrap">
                                Same return location
                              </span>
                            </label>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Pick-up Location *
                              </label>
                              <CustomSelect
                                value={formData.pickupLocation}
                                onChange={(e) => {
                                  const newValue = e.target.value;
                                  setFormData((prev) => ({
                                    ...prev,
                                    pickupLocation: newValue,
                                    customPickupLocation:
                                      newValue !== "other"
                                        ? ""
                                        : prev.customPickupLocation,
                                    ...(sameLocation
                                      ? {
                                          dropoffLocation: newValue,
                                          customDropoffLocation:
                                            newValue !== "other"
                                              ? ""
                                              : prev.customPickupLocation,
                                        }
                                      : {}),
                                  }));
                                }}
                                onOtherLocationChange={(value) => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    customPickupLocation: value,
                                    ...(sameLocation
                                      ? { customDropoffLocation: value }
                                      : {}),
                                  }));
                                }}
                                options={locationOptions}
                                placeholder="Select location"
                                showOtherInput={true}
                                initialCustomValue={
                                  formData.customPickupLocation
                                }
                                disabled={!selectedCar}
                              />
                              {errors.pickupLocation && (
                                <p className="text-red-500 text-xs mt-1">
                                  {errors.pickupLocation}
                                </p>
                              )}
                            </div>

                            {!sameLocation && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Drop-off Location *
                                </label>
                                <CustomSelect
                                  value={formData.dropoffLocation}
                                  onChange={(e) => {
                                    setFormData((prev) => ({
                                      ...prev,
                                      dropoffLocation: e.target.value,
                                      customDropoffLocation:
                                        e.target.value !== "other"
                                          ? ""
                                          : prev.customDropoffLocation,
                                    }));
                                  }}
                                  onOtherLocationChange={(value) => {
                                    setFormData((prev) => ({
                                      ...prev,
                                      customDropoffLocation: value,
                                    }));
                                  }}
                                  options={locationOptions}
                                  placeholder="Select location"
                                  showOtherInput={true}
                                  initialCustomValue={
                                    formData.customDropoffLocation
                                  }
                                  disabled={!selectedCar}
                                />
                                {errors.dropoffLocation && (
                                  <p className="text-red-500 text-xs mt-1">
                                    {errors.dropoffLocation}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Customer Information */}
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                          <h4 className="font-medium text-gray-900 mb-4">
                            Customer Information
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                First Name *
                              </label>
                              <input
                                type="text"
                                placeholder="First Name"
                                value={formData.firstName}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    firstName: e.target.value,
                                  })
                                }
                                disabled={!selectedCar}
                                className={`w-full px-4 py-2 border ${
                                  errors.firstName
                                    ? "border-red-500"
                                    : "border-gray-300"
                                } rounded-md transition-all hover:border-blue-500 ${
                                  !selectedCar
                                    ? "bg-gray-100 cursor-not-allowed"
                                    : ""
                                }`}
                              />
                              {errors.firstName && (
                                <p className="text-red-500 text-xs mt-1">
                                  {errors.firstName}
                                </p>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Last Name *
                              </label>
                              <input
                                type="text"
                                placeholder="Last Name"
                                value={formData.lastName}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    lastName: e.target.value,
                                  })
                                }
                                disabled={!selectedCar}
                                className={`w-full px-4 py-2 border ${
                                  errors.lastName
                                    ? "border-red-500"
                                    : "border-gray-300"
                                } rounded-md transition-all hover:border-blue-500 ${
                                  !selectedCar
                                    ? "bg-gray-100 cursor-not-allowed"
                                    : ""
                                }`}
                              />
                              {errors.lastName && (
                                <p className="text-red-500 text-xs mt-1">
                                  {errors.lastName}
                                </p>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Email Address *
                              </label>
                              <input
                                type="email"
                                placeholder="Email Address"
                                value={formData.email}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    email: e.target.value,
                                  })
                                }
                                disabled={!selectedCar}
                                className={`w-full px-4 py-2 border ${
                                  errors.email
                                    ? "border-red-500"
                                    : "border-gray-300"
                                } rounded-md transition-all hover:border-blue-500 ${
                                  !selectedCar
                                    ? "bg-gray-100 cursor-not-allowed"
                                    : ""
                                }`}
                              />
                              {errors.email && (
                                <p className="text-red-500 text-xs mt-1">
                                  {errors.email}
                                </p>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Phone Number *
                              </label>
                              <input
                                type="tel"
                                placeholder="Phone Number"
                                value={formData.phone}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    phone: e.target.value,
                                  })
                                }
                                disabled={!selectedCar}
                                className={`w-full px-4 py-2 border ${
                                  errors.phone
                                    ? "border-red-500"
                                    : "border-gray-300"
                                } rounded-md transition-all hover:border-blue-500 ${
                                  !selectedCar
                                    ? "bg-gray-100 cursor-not-allowed"
                                    : ""
                                }`}
                              />
                              {errors.phone && (
                                <p className="text-red-500 text-xs mt-1">
                                  {errors.phone}
                                </p>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Age *
                              </label>
                              <select
                                value={formData.age}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    age: e.target.value,
                                  })
                                }
                                disabled={!selectedCar}
                                className={`w-full h-10 px-3 border border-gray-200 rounded bg-white appearance-none hover:border-blue-500 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                  !selectedCar
                                    ? "bg-gray-100 cursor-not-allowed"
                                    : ""
                                }`}
                              >
                                {Array.from(
                                  { length: 9 },
                                  (_, i) => 18 + i
                                ).map((age) => (
                                  <option key={age} value={age}>
                                    {age} years old
                                  </option>
                                ))}
                                <option value="26+">26+ years old</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        {/* Additional Options & Coupon */}
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                          <h4 className="font-medium text-gray-900 mb-4">
                            Additional Options & Discounts
                          </h4>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <p className="text-sm font-medium text-gray-700 mb-2">
                                Additional Options (Free):
                              </p>
                              <div className="space-y-2">
                                <CustomCheckbox
                                  checked={formData.additionalOptions.childSeat}
                                  onChange={(e) =>
                                    setFormData({
                                      ...formData,
                                      additionalOptions: {
                                        ...formData.additionalOptions,
                                        childSeat: e.target.checked,
                                      },
                                    })
                                  }
                                  label="Child Seat"
                                  disabled={!selectedCar}
                                />
                                <CustomCheckbox
                                  checked={formData.additionalOptions.babyChair}
                                  onChange={(e) =>
                                    setFormData({
                                      ...formData,
                                      additionalOptions: {
                                        ...formData.additionalOptions,
                                        babyChair: e.target.checked,
                                      },
                                    })
                                  }
                                  label="Baby Chair"
                                  disabled={!selectedCar}
                                />
                                <CustomCheckbox
                                  checked={formData.additionalOptions.gps}
                                  onChange={(e) =>
                                    setFormData({
                                      ...formData,
                                      additionalOptions: {
                                        ...formData.additionalOptions,
                                        gps: e.target.checked,
                                      },
                                    })
                                  }
                                  label="GPS"
                                  disabled={!selectedCar}
                                />
                              </div>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Coupon Code
                              </label>
                              <div className="relative">
                                <input
                                  type="text"
                                  placeholder="Enter coupon code"
                                  value={formData.couponCode}
                                  onChange={(e) => {
                                    const code = e.target.value.toUpperCase();
                                    setFormData({
                                      ...formData,
                                      couponCode: code,
                                    });
                                  }}
                                  onBlur={() =>
                                    validateCoupon(formData.couponCode)
                                  }
                                  disabled={!selectedCar}
                                  className={`w-full px-4 py-2 border rounded-md transition-all ${
                                    couponStatus.isValid
                                      ? "border-green-500 focus:ring-green-500"
                                      : couponStatus.message
                                      ? "border-red-500 focus:ring-red-500"
                                      : "border-gray-200 focus:ring-blue-500 hover:border-blue-500"
                                  } ${
                                    !selectedCar
                                      ? "bg-gray-100 cursor-not-allowed"
                                      : ""
                                  }`}
                                />
                                {couponStatus.message && (
                                  <div
                                    className={`text-sm mt-1 ${
                                      couponStatus.isValid
                                        ? "text-green-600"
                                        : "text-red-600"
                                    }`}
                                  >
                                    {couponStatus.message}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Additional Notes:
                            </label>
                            <textarea
                              value={formData.notes}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  notes: e.target.value,
                                })
                              }
                              rows="3"
                              disabled={!selectedCar}
                              className={`w-full p-3 border border-gray-200 rounded bg-white hover:border-blue-500 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                !selectedCar
                                  ? "bg-gray-100 cursor-not-allowed"
                                  : ""
                              }`}
                              placeholder="Any special requests or notes"
                            />
                          </div>
                        </div>

                        {/* Price Breakdown */}
                        <div className="bg-blue-900 p-4 rounded-lg shadow-lg text-white">
                          <h4 className="font-medium text-white mb-4 flex items-center">
                            <FaMoneyBillWave className="mr-2" />
                            Price Breakdown
                          </h4>

                          <div className="space-y-1">
                            {formData.pickupDate &&
                              formData.dropoffDate &&
                              selectedCar &&
                              calculatePriceBreakdown()?.map(
                                (period, index) => (
                                  <div
                                    key={index}
                                    className="flex justify-between items-center text-sm"
                                  >
                                    <span>
                                      {period.days > 1
                                        ? `${formatDateRange(
                                            period.startDate
                                          )} - ${formatDateRange(
                                            period.endDate
                                          )}`
                                        : formatDateRange(
                                            period.startDate
                                          )}{" "}
                                      ({period.days} day
                                      {period.days > 1 ? "s" : ""})
                                    </span>
                                    <span
                                      className={
                                        period.price !== selectedCar.basePrice
                                          ? "text-blue-300"
                                          : ""
                                      }
                                    >
                                      €{period.price}/day
                                    </span>
                                  </div>
                                )
                              )}

                            {formData.pickupDate &&
                              formData.dropoffDate &&
                              selectedCar && (
                                <>
                                  <div className="flex justify-between items-center text-sm pt-2 border-t border-blue-800">
                                    <span>Duration:</span>
                                    <span>
                                      {Math.ceil(
                                        Math.abs(
                                          new Date(formData.dropoffDate) -
                                            new Date(formData.pickupDate)
                                        ) /
                                          (1000 * 60 * 60 * 24)
                                      )}{" "}
                                      day(s)
                                    </span>
                                  </div>

                                  {/* Duration Discount */}
                                  {(() => {
                                    const days = Math.ceil(
                                      Math.abs(
                                        new Date(formData.dropoffDate) -
                                          new Date(formData.pickupDate)
                                      ) /
                                        (1000 * 60 * 60 * 24)
                                    );
                                    let discountPercentage = 0;
                                    if (days > 10) {
                                      discountPercentage =
                                        durationDiscounts.longTerm.discount;
                                    } else if (days > 3) {
                                      discountPercentage =
                                        durationDiscounts.mediumTerm.discount;
                                    } else {
                                      discountPercentage =
                                        durationDiscounts.shortTerm.discount;
                                    }
                                    if (discountPercentage > 0) {
                                      const subtotal =
                                        calculatePriceBreakdown()?.reduce(
                                          (total, period) =>
                                            total + period.price * period.days,
                                          0
                                        );
                                      const discountAmount =
                                        (subtotal * discountPercentage) / 100;
                                      return (
                                        <div className="flex justify-between items-center text-sm text-green-300">
                                          <span>
                                            Duration Discount (
                                            {discountPercentage}%):
                                          </span>
                                          <span>
                                            -€{discountAmount.toFixed(2)}
                                          </span>
                                        </div>
                                      );
                                    }
                                    return null;
                                  })()}

                                  <div className="flex justify-between items-center text-sm text-gray-200 pt-2 border-t border-blue-800">
                                    <span>Subtotal:</span>
                                    <span>
                                      €{(totalPrice / 1.24).toFixed(2)}
                                    </span>
                                  </div>

                                  {couponStatus.isValid && (
                                    <div className="flex justify-between items-center text-sm text-green-300">
                                      <span>Coupon Discount:</span>
                                      <span>
                                        -€{couponStatus.discount.toFixed(2)}
                                      </span>
                                    </div>
                                  )}

                                  <div className="flex justify-between items-center text-sm text-gray-200">
                                    <span>VAT (24%):</span>
                                    <span>
                                      €{((totalPrice / 1.24) * 0.24).toFixed(2)}
                                    </span>
                                  </div>

                                  <div className="flex justify-between items-center text-sm text-green-300">
                                    <span>
                                      Commission ({partner.commissionRate}%):
                                    </span>
                                    <span>
                                      €
                                      {(
                                        ((totalPrice / 1.24) *
                                          partner.commissionRate) /
                                        100
                                      ).toFixed(2)}
                                    </span>
                                  </div>

                                  <div className="flex justify-between items-center font-bold text-lg mt-2 pt-2 border-t border-blue-800">
                                    <span>Total:</span>
                                    <span>€{totalPrice.toFixed(2)}</span>
                                  </div>
                                </>
                              )}
                          </div>
                        </div>

                        {errors.submit && (
                          <p className="text-red-500 text-center">
                            {errors.submit}
                          </p>
                        )}

                        {/* Submit Button */}
                        <div className="flex justify-end">
                          <button
                            type="submit"
                            disabled={loading || !selectedCar}
                            className="px-6 py-3 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                          >
                            {loading ? (
                              <>
                                <LoadingSpinner
                                  size="sm"
                                  className="text-white"
                                />
                                <span>Creating Rental...</span>
                              </>
                            ) : (
                              <span>Create Rental</span>
                            )}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        <style jsx="true">{`
          .react-datepicker__day--selected {
            background-color: #1e3a8a !important;
            color: white !important;
          }

          .react-datepicker__time-list-item--selected {
            background-color: #1e3a8a !important;
            color: white !important;
          }
        `}</style>
      </motion.div>
    </div>
  );
};

export default CreatePartnerRentalModal;
